/**
 * ## Ours (Base UI)
 * Animated sliding indicator, `orientation` prop (`horizontal` | `vertical`).
 * * `TabsList` — tab bar; renders the `<Indicator>` automatically
 * * `TabsTab` / `TabsTrigger` — tab button; `disabled` prop supported
 * * `TabsPanel` / `TabsContent` — associated panel
 *
 * ## Shadcn UI
 * Static pill/segmented style, no animated indicator.
 * * `TabsList` — pill container
 * * `TabsTrigger` — tab button
 * * `TabsContent` — associated panel
 */

'use client'

import { Tabs as TabsPrimitive } from '@base-ui/react/tabs'
import { cn } from '../lib/utils'

function Tabs({ className, ...props }: TabsPrimitive.Root.Props) {
  return (
    <TabsPrimitive.Root
      className={cn(
        'flex flex-col data-[orientation=vertical]:flex-row',
        className,
      )}
      data-slot="tabs"
      {...props}
    />
  )
}

function TabsList({ className, children, ...props }: TabsPrimitive.List.Props) {
  return (
    <TabsPrimitive.List
      className={cn(
        'text-on-surface relative z-0 flex w-fit items-center justify-center gap-x-0.5',
        'data-[orientation=horizontal]:py-1 data-[orientation=vertical]:flex-col data-[orientation=vertical]:px-1',
        className,
      )}
      data-slot="tabs-list"
      {...props}
    >
      {children}
      <TabsPrimitive.Indicator
        className={cn(
          'absolute bottom-0 left-0 h-(--active-tab-height) w-(--active-tab-width) translate-x-(--active-tab-left) -translate-y-(--active-tab-bottom) transition-[width,translate] duration-200 ease-in-out',
          'bg-primary z-10 rounded-full data-[orientation=horizontal]:h-0.5 data-[orientation=horizontal]:translate-y-px data-[orientation=vertical]:w-0.5 data-[orientation=vertical]:-translate-x-px',
        )}
        data-slot="tab-indicator"
      />
    </TabsPrimitive.List>
  )
}

function TabsTab({ className, disabled, ...props }: TabsPrimitive.Tab.Props) {
  return (
    <TabsPrimitive.Tab
      disabled={disabled}
      className={cn(
        "text-on-surface data-active:text-primary focus-visible:not-data-disabled:ring-primary/80 relative flex shrink-0 grow cursor-pointer items-center justify-center gap-1.5 rounded-[24px] px-3 px-[calc(--spacing(2.5)-1px)] py-2 text-sm font-medium whitespace-nowrap transition-[color,background-color] outline-none select-none focus-visible:not-data-disabled:ring-2 data-disabled:cursor-not-allowed! data-disabled:opacity-60 data-[orientation=vertical]:w-full data-[orientation=vertical]:justify-start [&_svg]:pointer-events-none [&_svg]:-mx-0.5 [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
        className,
      )}
      data-slot="tabs-tab"
      {...props}
    />
  )
}

function TabsPanel({ className, ...props }: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel
      className={cn('flex-1 outline-none', className)}
      data-slot="tabs-content"
      {...props}
    />
  )
}

export {
  Tabs,
  TabsList,
  TabsTab,
  TabsTab as TabsTrigger,
  TabsPanel,
  TabsPanel as TabsContent,
}
